import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import Seo from '../../components/Seo';
import { ALL_STATES } from './allStates';
import { MapPin, Briefcase, Search, ArrowRight, Building2, GraduationCap, Loader2 } from 'lucide-react';

const LICENSES = [
  { code: 'LCSW', label: 'LCSW Jobs' },
  { code: 'LMSW', label: 'LMSW Jobs' },
  { code: 'LBSW', label: 'LBSW Jobs' },
  { code: 'SWA', label: 'Social Work Assistant' },
];

const StateJobsPage = () => {
  const { state } = useParams();
  const data = ALL_STATES[state];
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!data) return;
    setLoading(true);
    axios.get(`${API_URL}/jobs`, { params: { location: data.abbr, limit: 12 } })
      .then((r) => { setJobs(r.data.jobs || []); setTotal(r.data.total ?? (r.data.jobs || []).length); })
      .catch(() => { setJobs([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [state, data]);

  if (!data) return <Navigate to="/jobs" replace />;

  const { name, abbr, cities } = data;
  const canonicalPath = `/social-work-jobs/${state}`;
  const title = `Social Work Jobs in ${name} | LCSW, LMSW & LBSW Careers`;
  const description = `Find licensed social worker jobs in ${name} — LCSW, LMSW, LBSW and social work assistant roles across ${cities.slice(0, 3).join(', ')} and statewide. Browse and apply on I Need A Social Worker.`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: title,
    description,
    url: `https://ineedasocialworker.com${canonicalPath}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: LICENSES.map((l, i) => ({
        '@type': 'ListItem', position: i + 1, name: `${l.code} jobs in ${name}`,
      })),
    },
  };
  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: `How do I find social work jobs in ${name}?`,
        acceptedAnswer: { '@type': 'Answer', text: `Browse current LCSW, LMSW, LBSW and social work assistant openings in ${name} on I Need A Social Worker, filter by city or license, and apply directly.` } },
      { '@type': 'Question', name: `What licenses are needed for social work jobs in ${name}?`,
        acceptedAnswer: { '@type': 'Answer', text: `Most clinical roles in ${name} require an LCSW; case management and generalist roles may accept LMSW or LBSW. Check each posting for its required license.` } },
    ],
  };

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16" data-testid="state-jobs-page">
      <Seo exactTitle={`${title} | I Need A Social Worker`} description={description} path={canonicalPath} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* Hero */}
      <section className="bg-primary pt-24 pb-12 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-secondary font-semibold uppercase tracking-widest text-xs flex items-center gap-2"><MapPin className="w-4 h-4" /> {name} · {abbr}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mt-2">
            Social Work Jobs in <span className="text-secondary">{name}</span>
          </h1>
          <p className="mt-4 text-white/85 text-lg max-w-2xl">
            LCSW, LMSW, LBSW and social work assistant opportunities across {name} — hospitals, schools, hospice, behavioral health, medical and community settings.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to={`/jobs?location=${encodeURIComponent(abbr)}`}>
              <Button className="bg-secondary hover:bg-secondary-hover text-white h-11 px-6" data-testid="state-browse-all">
                <Search className="w-4 h-4 mr-2" /> Browse all {name} jobs
              </Button>
            </Link>
            <Link to="/employer/post-job">
              <Button variant="outline" className="bg-white/10 border-white/40 text-white hover:bg-white/20 h-11 px-6" data-testid="state-post-job">
                <Building2 className="w-4 h-4 mr-2" /> Hiring in {name}? Post a job
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* License filters */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <h2 className="font-serif text-2xl font-bold text-primary mb-4">Browse by license in {name}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {LICENSES.map((l) => (
            <Link key={l.code} to={`/jobs?license_type=${l.code}&location=${encodeURIComponent(abbr)}`}
              className="bg-white rounded-xl border border-gray-100 p-4 hover:border-secondary hover:shadow-md transition-all group" data-testid={`state-license-${l.code}`}>
              <Briefcase className="w-5 h-5 text-secondary mb-2" />
              <p className="font-semibold text-primary">{l.label}</p>
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">in {name} <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" /></p>
            </Link>
          ))}
        </div>
      </section>

      {/* Cities */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <h2 className="font-serif text-2xl font-bold text-primary mb-4">Social work jobs by city</h2>
        <div className="flex flex-wrap gap-2">
          {cities.map((c) => (
            <Link key={c} to={`/jobs?location=${encodeURIComponent(c)}`}
              className="px-4 py-2 rounded-full text-sm bg-white border border-gray-200 text-gray-700 hover:border-secondary hover:text-primary transition-colors" data-testid={`state-city-${c}`}>
              <MapPin className="w-3.5 h-3.5 inline mr-1 text-secondary" /> {c}
            </Link>
          ))}
        </div>
      </section>

      {/* Live jobs preview */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl font-bold text-primary">Latest openings in {name}</h2>
          <Link to={`/jobs?location=${encodeURIComponent(abbr)}`} className="text-sm text-primary hover:underline flex items-center gap-1">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {loading ? (
          <div className="text-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : jobs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center" data-testid="state-jobs-empty">
            <GraduationCap className="w-10 h-10 text-secondary mx-auto mb-3" />
            <h3 className="font-serif text-xl font-bold text-primary">New {name} roles are added regularly</h3>
            <p className="text-gray-500 mt-2 max-w-md mx-auto">Set a job alert or browse nationwide social work jobs while more {name} listings come online.</p>
            <div className="mt-5 flex justify-center gap-3">
              <Link to="/jobs"><Button className="bg-primary hover:bg-primary/90 text-white">Browse all jobs</Button></Link>
              <Link to="/register"><Button variant="outline" className="border-primary text-primary">Create a job alert</Button></Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="state-jobs-grid">
            {jobs.map((j) => (
              <Link key={j.job_id} to={`/jobs/${j.job_id}`} className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-lg hover:border-secondary/40 transition-all block">
                <h3 className="font-serif font-bold text-primary leading-snug line-clamp-2">{j.title}</h3>
                <p className="text-sm text-gray-500 mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {j.location || name}</p>
                {j.employer_name && <p className="text-xs text-gray-400 mt-2">{j.employer_name}</p>}
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* SEO copy */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <div className="bg-white rounded-2xl border border-gray-100 p-8">
          <h2 className="font-serif text-2xl font-bold text-primary">Social work careers in {name}</h2>
          <p className="text-gray-600 mt-3 leading-relaxed">
            {name} employs licensed social workers across hospitals, hospice and home health, K-12 schools, behavioral health and substance-use programs, child welfare, veterans services, and community agencies.
            Whether you hold an LCSW, LMSW, or LBSW — or are starting as a social work assistant — I Need A Social Worker connects you with vetted, social-work-specific roles in {cities.slice(0, 4).join(', ')} and statewide.
          </p>
          <p className="text-gray-600 mt-3 leading-relaxed">
            Employers hiring in {name} can post social work jobs directly and reach a nationwide network of licensed candidates.
          </p>
        </div>
      </section>
    </div>
  );
};

export default StateJobsPage;
